import { MaterialIconName } from '@hyphengroup/cms-components/types/materialIconName';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  countDigitsBefore,
  findCursorPosition,
  formatCurrency,
  formatInputValue,
} from './utils';
import { AlphaTextInput } from '$/modules/material-tailwind/AlphaTextInput';

type AmountInputProps = {
  id?: string;
  value: number;
  currencySymbol: string;
  decimals: number;
  onChange: (value: number) => void;
  error?: boolean;
  errorMessage?: string;
  defaultValue?: number;
};

export const AmountInput = (props: AmountInputProps) => {
  const {
    value,
    id,
    currencySymbol,
    decimals,
    onChange,
    error,
    errorMessage,
    defaultValue = 0,
  } = props;

  const [innerValue, setInnerValue] = useState<string>(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef(false);
  const pendingCursorRef = useRef<number | null>(null);

  // Sync innerValue from parent value prop — but only when NOT focused,
  // to avoid overwriting the user's in-progress typing with formatCurrency/toFixed.
  useEffect(() => {
    if (!isFocusedRef.current) {
      setInnerValue(formatCurrency(value, '', decimals));
    }
  }, [value, decimals]);

  // Restore cursor position synchronously after React re-renders with the new formatted value.
  useLayoutEffect(() => {
    if (pendingCursorRef.current !== null && inputRef.current) {
      const pos = pendingCursorRef.current;
      pendingCursorRef.current = null;
      inputRef.current.setSelectionRange(pos, pos);
    }
  }, [innerValue]);

  const handleLoanAmountFocus = () => {
    isFocusedRef.current = true;
  };

  const handleLoanAmountBlur = () => {
    isFocusedRef.current = false;

    // Remove commas before converting to number
    const newValue = innerValue.replace(/,/g, '');
    let newNumber = Number(newValue);
    if (!newValue || isNaN(newNumber)) {
      newNumber = defaultValue;
    }

    onChange(newNumber);

    setInnerValue(formatCurrency(newNumber, '', decimals));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const rawCursorPos = e.target.selectionStart ?? rawValue.length;

    // Format the value with commas
    const formatted = formatInputValue(rawValue, decimals);

    // Calculate where the cursor should be in the formatted string
    let newCursorPos: number;

    if (decimals > 0) {
      const rawDecimalIndex = rawValue.indexOf('.');
      const formattedDecimalIndex = formatted.indexOf('.');

      // If both raw and formatted values have a decimal point and the cursor
      // is after the decimal in the raw string, preserve the offset from the decimal.
      if (
        rawDecimalIndex !== -1 &&
        formattedDecimalIndex !== -1 &&
        rawCursorPos > rawDecimalIndex
      ) {
        const offsetAfterDecimal = rawCursorPos - rawDecimalIndex;
        newCursorPos = Math.min(
          Math.max(formattedDecimalIndex + offsetAfterDecimal, 0),
          formatted.length,
        );
      } else {
        // Fallback: use digit-based mapping for integer part / no decimal
        const digitsBefore = countDigitsBefore(rawValue, rawCursorPos);
        newCursorPos = findCursorPosition(formatted, digitsBefore);
      }
    } else {
      // Integer-only: preserve existing digit-based behavior
      const digitsBefore = countDigitsBefore(rawValue, rawCursorPos);
      newCursorPos = findCursorPosition(formatted, digitsBefore);
    }

    pendingCursorRef.current = newCursorPos;
    setInnerValue(formatted);
  };

  // Simple solution, can be improved later if needed
  // should be synced with the currency symbol mapping in PersonalLoanCalculator.tsx
  const useGoogleMaterialIconFont = currencySymbol === '$';
  const MONEY_ICON: MaterialIconName = 'attach_money';
  const prefix = useGoogleMaterialIconFont ? MONEY_ICON : currencySymbol;

  return (
    <AlphaTextInput
      ref={inputRef}
      id={id}
      autoComplete="off"
      // eslint-disable-next-line tailwindcss/no-custom-classname, tailwindcss/no-arbitrary-value
      className="!border-1 !mhg-alpha-form-input flex h-button/size items-center self-stretch [appearance:textfield] read-only:bg-[--highlights-gray-light] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      prefixClassName={classNames('font-font-weight/normal text-gray-600', {
        'material-symbols-outlined text-font-size/display-1 !left-3':
          useGoogleMaterialIconFont,
      })}
      icon=""
      inputMode={decimals === 0 ? 'numeric' : 'decimal'}
      type="text"
      value={innerValue}
      onFocus={handleLoanAmountFocus}
      onBlur={handleLoanAmountBlur}
      onChange={handleChange}
      prefix={prefix}
      placeholder={
        defaultValue
          ? formatCurrency(defaultValue, currencySymbol, decimals)
          : '0'
      }
      required
      error={error}
      hideValidation={!errorMessage}
      setCustomValidity={() => errorMessage || ''}
    />
  );
};
